// ============================================================================
// Clarity Chat (Enhanced Bot / Messaging for Web) - RENEWAL portal
// renewal.claritybenefitsolutions.com
// Deployment: Workflow_Platform_Chat_Enhanced
//
// Tracking_Number rule:
//   "xyz"          -> authentication REQUIRED (bot verifies the user)
//   anything else  -> authentication NOT required (user is already logged in)
// Renewal users are logged in, so Tracking_Number = "111".
// ============================================================================
import { useEffect } from "react";

const ORG_ID = "00Df4000003kFDv";
const DEPLOYMENT_NAME = "Workflow_Platform_Chat_Enhanced";
const SITE_URL =
  "https://claritybenefitsolutions.my.site.com/ESWWorkflowPlatformChat1772802755379";
const SCRT2_URL = "https://claritybenefitsolutions.my.salesforce-scrt.com";
const SCRIPT_ID = "clarity-embedded-messaging-bootstrap";

// ===== Chat session cleanup (added) =====
const ENDED_KEY = "clarityChatEnded";
const RELOAD_KEY = "clarityChatFallbackAt";

// Deletes the conversation saved in the browser (does NOT remove the live chat).
// Call clarityChatLogout() from the Renewal logout handler, before redirecting to login.
export const clarityChatLogout = () => {
  [window.localStorage, window.sessionStorage].forEach((store) => {
    try {
      Object.keys(store).forEach((key) => {
        if (key.indexOf(ORG_ID) === 0 || key.indexOf("_WEB_STORAGE") !== -1) {
          store.removeItem(key);
        }
      });
    } catch (e) {
      /* storage blocked */
    }
  });
  try {
    localStorage.removeItem(ENDED_KEY);
  } catch (e) {
    /* storage blocked */
  }
};
window.clarityChatLogout = clarityChatLogout;

const ChatInvitation = () => {
  useEffect(() => {
    const email = sessionStorage.getItem("username") || "";
    const firstName = sessionStorage.getItem("firstname") || "Guest";
    const lastName = sessionStorage.getItem("lastName") || "";

    // Fires once the chat client is ready
    const handleReady = () => {
      console.log("Embedded Messaging Ready");

      // Hidden pre-chat values must be strings -> use "" when unknown
      window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields({
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Phone: "",
        ZipCode: "",
        DOB: "",
        Tracking_Number: "111", // NOT "xyz" -> authentication not required
        Language: "en_US",
      });
    };

    // init() may only run once per page load (guards against re-mounts / StrictMode)
    const initEmbeddedMessaging = () => {
      if (window.__clarityChatInitialized) return;
      window.__clarityChatInitialized = true;

      try {
        window.embeddedservice_bootstrap.settings.language = "en_US";
        window.embeddedservice_bootstrap.init(ORG_ID, DEPLOYMENT_NAME, SITE_URL, {
          scrt2URL: SCRT2_URL,
        });
      } catch (err) {
        window.__clarityChatInitialized = false;
        console.error("Error loading Embedded Messaging:", err);
      }
    };


    // ===== Chat ended (added) =====
    // Before the chat loads: if the last conversation ended, start fresh
    if (!window.embeddedservice_bootstrap) {
      let ended = false;
      try {
        ended = localStorage.getItem(ENDED_KEY) === "1";
      } catch (e) {
        /* storage blocked */
      }
      if (ended) clarityChatLogout();
    }

    // Chat ended: clear the session and load a fresh chat on the same page (no reload).
    // If the fresh chat is not ready within 8 seconds, reload the page once as a fallback.
    let clarityEndTimer = null;
    let clarityFallbackTimer = null;
    let clarityRestarting = false;

    const restartChat = () => {
      let ready = false;
      const onReady = () => {
        ready = true;
        window.removeEventListener("onEmbeddedMessagingReady", onReady);
        clarityRestarting = false;
        console.log("[ClarityChat] New chat session ready (no page reload)");
      };
      window.addEventListener("onEmbeddedMessagingReady", onReady);

      try {
        window.embeddedservice_bootstrap?.utilAPI?.removeAllComponents?.();
      } catch (e) {
        console.warn("[ClarityChat] Remove failed:", e);
      }
      document.getElementById("embedded-messaging")?.remove();
      clarityChatLogout();

      try {
        delete window.embeddedservice_bootstrap;
      } catch (e) {
        window.embeddedservice_bootstrap = undefined;
      }
      document.getElementById(SCRIPT_ID)?.remove();
      window.__clarityChatInitialized = false;

      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = `${SITE_URL}/assets/js/bootstrap.min.js`;
      script.async = true;
      script.onload = initEmbeddedMessaging;
      document.body.appendChild(script);

      clarityFallbackTimer = setTimeout(() => {
        if (ready) return;
        window.removeEventListener("onEmbeddedMessagingReady", onReady);
        let last = 0;
        try {
          last = parseInt(sessionStorage.getItem(RELOAD_KEY) || "0", 10);
        } catch (e) {
          /* storage blocked */
        }
        if (Date.now() - last < 30000) {
          clarityRestarting = false;
          console.warn("[ClarityChat] Restart failed; not reloading again so soon");
          return;
        }
        try {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        } catch (e) {
          /* storage blocked */
        }
        console.warn("[ClarityChat] Restart failed; reloading page as fallback");
        window.location.reload();
      }, 8000);
    };

    const handleConversationClosed = () => {
      try {
        localStorage.setItem(ENDED_KEY, "1");
      } catch (e) {
        /* storage blocked */
      }
      if (clarityRestarting) return;
      clarityRestarting = true;
      clarityEndTimer = setTimeout(restartChat, 2000);
    };
    window.addEventListener("onEmbeddedMessagingConversationClosed", handleConversationClosed);

    // Register the listener BEFORE loading/initialising so the ready event isn't missed
    window.addEventListener("onEmbeddedMessagingReady", handleReady);

    if (window.embeddedservice_bootstrap) {
      initEmbeddedMessaging();
    } else if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = `${SITE_URL}/assets/js/bootstrap.min.js`;
      script.async = true;
      script.onload = initEmbeddedMessaging;
      document.body.appendChild(script);
    }

    return () => {
      window.removeEventListener("onEmbeddedMessagingReady", handleReady);
      window.removeEventListener("onEmbeddedMessagingConversationClosed", handleConversationClosed);
      clearTimeout(clarityEndTimer);
      clearTimeout(clarityFallbackTimer);
    };
  }, []);

  return null;
};

export default ChatInvitation;
